import { useQuery, useQueryClient } from '@tanstack/react-query'
import Page from '../../components/layouts/page'
import { useParams } from 'react-router-dom'
import useStore from '../../hooks/use_store'
import useLogoUpload from '../../hooks/use_logo_upload'
import { TextField, Combobox, Select, Toggle, FileUpload } from '@cinesend/ui-kit'
import useAuth from '../../hooks/use_auth.jsx'
import { useEffect, useState } from 'react'

const Organization = () => {
  const { orgID } = useParams()

  const [org, setOrg] = useState({})

  const queryKey = ['organizations/', orgID]

  const { updateModel } = useStore()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: organization, isLoading } = useQuery(queryKey)
  const { data: { data: users } = { data: [] } } = useQuery(['users'])
  const { data: airlines } = useQuery(['airlines'])

  const update = (value, isLocal) => {
    if (isLocal) {
      setOrg({ ...org, ...value })
    } else {
      updateModel.mutate({
        endpoint: 'organizations',
        id: orgID,
        data: value,
      })
    }
  }

  const logoUpload = useLogoUpload({
    url: `${import.meta.env.VITE_API_URL}/api/organizations/upload-logo/${orgID}`,
    onComplete: () => {
      queryClient.fetchQuery(queryKey).then((res) => setOrg(res))
      queryClient.invalidateQueries(['/user'])
    },
  })

  useEffect(() => {
    setOrg(organization)
  }, [organization])

  return (
    <Page breadcrumbs={[{ text: 'Organizations', to: '/settings/organizations' }, { text: organization?.name }]}>
      <div className='flex flex-col space-y-4'>
        <TextField
          value={org?.name ?? ''}
          label='Name'
          onChange={(e) => update({ name: e.target.value }, true)}
          onBlur={() => update({ name: org?.name }, false)}
        />
        <Select
          fullWidth
          label='Owner'
          value={org?.owner_id ? String(org.owner_id) : null}
          disabled={isLoading}
          options={users?.map((opt) => ({ value: String(opt.id), label: opt.name })) || []}
          onValueChange={(id) => update({ owner_id: Number(id) }, false)}
        />
        <Combobox
          label='Airlines'
          options={airlines?.map((item) => ({ value: String(item.id), label: item.name })) ?? []}
          multiple
          value={org?.airlines?.map((airline) => String(airline.id)) ?? []}
          onValueChange={(ids) => update({ airlines: ids.map(Number) }, false)}
        />
      </div>
      {user?.role_id === 1 && (
        <div className='flex flex-col space-y-4 py-4'>
          <FileUpload
            className='h-40'
            accept='.jpg,.jpeg,.png'
            message='Drop image here'
            buttonLabel='Upload Logo'
            imageUrl={org?.logo_url}
            onFileSelected={logoUpload.upload}
            uploading={logoUpload.uploading}
            error={logoUpload.error}
            onRemove={() => {
              updateModel.mutate({ endpoint: 'organizations', id: orgID, data: { logo_url: '' } })
              setOrg((prev) => ({ ...prev, logo_url: '' }))
              queryClient.invalidateQueries(['/user'])
            }}
          />
          <Toggle
            pressed={!!org?.oidc_enabled}
            text={'Enable OpenID Connect for this organization'}
            onToggle={() => update({ oidc_enabled: !org?.oidc_enabled }, false)}
          />
          <TextField
            disabled={!org?.oidc_enabled}
            label={'Domain Name'}
            name='host_name'
            value={org?.host_name ?? ''}
            onChange={(e) => update({ host_name: e.target.value }, true)}
            onBlur={() => update({ host_name: org?.host_name }, false)}
          />

          <TextField
            disabled={!org?.oidc_enabled}
            label={'OIDC Issuer URL'}
            name='oidc_issuer_url'
            value={org?.oidc_issuer_url ?? ''}
            onChange={(e) => update({ oidc_issuer_url: e.target.value }, true)}
            onBlur={() => update({ oidc_issuer_url: org?.oidc_issuer_url }, false)}
          />
          <TextField
            disabled={!org?.oidc_enabled}
            label={'OIDC Client ID'}
            name='oidc_client_id'
            value={org?.oidc_client_id ?? ''}
            onChange={(e) => update({ oidc_client_id: e.target.value }, true)}
            onBlur={() => update({ oidc_client_id: org?.oidc_client_id }, false)}
          />
          <TextField
            disabled={!org?.oidc_enabled}
            label={'OIDC Client Secret'}
            name='oidc_client_secret'
            value={org?.oidc_client_secret ?? ''}
            onChange={(e) => update({ oidc_client_secret: e.target.value }, true)}
            onBlur={() => update({ oidc_client_secret: org?.oidc_client_secret }, false)}
          />
        </div>
      )}
    </Page>
  )
}

export default Organization
